import React, { createContext, useState, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { AuthContextProps, AuthProviderProps, AuthContextState, User, LoginData, LogoutData } from "../interfaces";
import { fetchCsrfToken, getIdApp, getEmail, getInviteKey, referer, getDeviceId, ensureCsrfToken } from "../utils";
import {
  authAppIdCookieName,
  generateRequestConfig,
} from "../constants";
import {buildAuthCallbackUrl} from "../authCallbackUrl";
import { resolveLoginReturnUrl } from "../authReturnUrl";

// docs/security-and-platform-work-plan.md Этап 7 — access/refresh tokens are
// now HttpOnly cookies the backend itself sets/reads (see
// idexpress_shared.api.session_cookies); this frontend never sees their
// values and never writes them. "Am I logged in, and as whom" now comes
// from asking the backend (/activesession) instead of decoding a JWT the
// browser can no longer read.
const initialAuthContextState: AuthContextState = {
  user: { email: null, name: null },
  csrf_token: null,
  login: null,
  logout: null,
  accessToken: null,
  idApp: getIdApp(),
  inviteKey: getInviteKey(),
  inviteEmail: getEmail(),
  referer: referer(),
  refreshTimeoutId: null,
  isAuthenticated: false,
};

export const AuthContext = createContext<AuthContextProps>({
  AuthContextState: initialAuthContextState,
  handleLogIn: () => {},
  handleLogOut: () => {},
  redirectToApp: () => {},
  checkIsAuthenticated: () => {},
  setAuthContextState: () => {},
  oneClickLogin: async () => false,
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [AuthContextState, setAuthContextState] = useState<AuthContextState>(initialAuthContextState);
  const resumeRefreshInProgressRef = useRef(false);
  
  const [csrfRefreshTimeoutId, setCsrfRefreshTimeoutId] = useState<NodeJS.Timeout | null>(null);

  const scheduleCsrfRefresh = (ms: number) => {
    if (csrfRefreshTimeoutId) {
      clearTimeout(csrfRefreshTimeoutId);
    }
    const timeoutId = setTimeout(() => {
      fetchCsrfToken(setAuthContextState, scheduleCsrfRefresh);
    }, ms);
    setCsrfRefreshTimeoutId(timeoutId);
  };

  const checkIsAuthenticated = async () => {
    // docs/security-and-platform-work-plan.md Этап 7 — the access token is
    // an HttpOnly cookie now; the browser sends it automatically
    // (withCredentials, see generateRequestConfig) but this code can no
    // longer read or decode it. /activesession is the backend's own answer
    // to "who is currently logged in".
    try {
      const response = await axios(generateRequestConfig("get", "activesession", {}));
      const user: User = { email: response.data?.email ?? null, name: null };
      await setAuthContextState((prevState) => ({
        ...prevState,
        user,
        isAuthenticated: true,
        referer: referer(),
        idApp: getIdApp(),
      }));
    } catch (error) {
      const user: User = { email: null, name: null };
      await setAuthContextState((prevState) => ({
        ...prevState,
        user,
        isAuthenticated: false,
        referer: referer(),
        idApp: getIdApp(),
      }));
    }
  };

  const scheduleTokenRefresh = () => {
    setAuthContextState((prevState) => {
      if (prevState.refreshTimeoutId !== null) {
        clearTimeout(prevState.refreshTimeoutId);
      }

      return { ...prevState, refreshTimeoutId: null };
    });

    const expirationTimeStr = localStorage.getItem("accessTokenExpAt");
    if (!expirationTimeStr) return;

    const refreshAt = new Date(expirationTimeStr).getTime() - 3 * 60 * 1000;
    const timeUntilRefresh = refreshAt - Date.now();

    if (timeUntilRefresh <= 0) {
      refreshToken().catch((error) => console.error("Error token refresh:", error));
      return;
    }

    const timeoutId = setTimeout(() => {
      refreshToken().catch((error) => console.error("Error token refresh:", error));
    }, timeUntilRefresh);

    setAuthContextState((prevState) => ({
      ...prevState,
      refreshTimeoutId: timeoutId,
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchCsrfToken(setAuthContextState, scheduleCsrfRefresh);
      checkIsAuthenticated();
      scheduleTokenRefresh();
    };
    fetchData();
  }, []);

  useEffect(() => {}, [AuthContextState]);

  const refreshToken = async () => {
    // docs/security-and-platform-work-plan.md Этап 7 — the refresh token is
    // an HttpOnly cookie the browser sends automatically; this code can no
    // longer read it beforehand to short-circuit an obviously-missing or
    // expired token, so it always attempts the call and lets the backend's
    // 401 (missing/expired/reused token — see idexpress_shared.api.refresh)
    // drive the logout path below.
    const requestConfigLogin = generateRequestConfig(
      "post",
      "refresh",
      {
        deviceId: getDeviceId(),
        appId: getIdApp(),
      },
      {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      }
    );

    try {
      const response = await axios(requestConfigLogin);
      const { accessTokenExpAt, refreshTokenExpAt } = response.data;

      localStorage.setItem("accessTokenExpAt", accessTokenExpAt.toString());
      localStorage.setItem("refreshTokenExpAt", refreshTokenExpAt.toString());

      await setAuthContextState((prevState) => ({
        ...prevState,
        isAuthenticated: true,
      }));

      scheduleTokenRefresh();
    } catch (error) {
      let user: User = { email: null, name: null };

      await setAuthContextState((prevState) => ({
        ...prevState,
        user: user,
        isAuthenticated: false,
        referer: referer(),
        idApp: getIdApp(),
      }));

      handleLogOut({});
    }
  };

  useEffect(() => {
    const refreshAfterResume = async () => {
      if (document.visibilityState !== "visible" || resumeRefreshInProgressRef.current) return;

      // docs/security-and-platform-work-plan.md Этап 7 — accessTokenExpAt
      // is the only client-visible signal left (non-sensitive, still
      // returned in the JSON body alongside the HttpOnly cookie); whether a
      // refresh token cookie actually exists can only be learned by asking.
      const expirationTimeStr = localStorage.getItem("accessTokenExpAt");
      const expirationTime = expirationTimeStr ? new Date(expirationTimeStr).getTime() : Number.NaN;
      const shouldRefresh =
        !Number.isFinite(expirationTime) ||
        expirationTime <= Date.now() + 3 * 60 * 1000;

      if (!shouldRefresh) {
        scheduleTokenRefresh();
        return;
      }

      resumeRefreshInProgressRef.current = true;
      try {
        await refreshToken();
      } finally {
        resumeRefreshInProgressRef.current = false;
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") void refreshAfterResume();
    };
    const handleFocus = () => void refreshAfterResume();

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleFocus);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
    };
  }, []);

  const redirectToApp = () => {
    const { login, logout } = AuthContextState;
    const ref = AuthContextState.referer || referer();

    if (ref) {
      try {
        if (login) {
          window.location.href = buildAuthCallbackUrl(ref, "login", login);
        }
        if (logout) {
          window.location.href = buildAuthCallbackUrl(ref, "logout", logout);
        }
      } catch (error) {
        console.error("Invalid authentication return URL", error);
      }
    }
  };

  const handleLogIn = async (data: LoginData) => {
    const login = data.login;
    const callbackUrl = resolveLoginReturnUrl(referer(), data);

    // docs/security-and-platform-work-plan.md Этап 7 — the backend already
    // set the HttpOnly access/refresh cookies on this same response
    // (idexpress_shared.auth.authorization.Authorization.authorize()); this
    // frontend only keeps the non-sensitive expiry timestamps, for
    // scheduling its own refresh calls.
    if (data.accessToken && login) {
      localStorage.setItem("accessTokenExpAt", data.accessTokenExpAt.toString());
      localStorage.setItem("refreshTokenExpAt", data.refreshTokenExpAt.toString());

      await setAuthContextState((prevState) => ({
        ...prevState,
        login: login,
        referer: callbackUrl,
        isAuthenticated: true,
      }));

      checkIsAuthenticated();
      scheduleTokenRefresh();

      if (callbackUrl) {
        Cookies.remove("referer");
        Cookies.remove(authAppIdCookieName);
        window.location.href = buildAuthCallbackUrl(callbackUrl, "login", login);
      }
    }
  };

  // Silent SSO (prompt=none): the same PUT /oneclicklogin -> handleLogIn
  // sequence Logout.tsx's "Continue" button already drives, pulled out
  // here so Login.tsx's silent path can reuse it without duplicating the
  // CSRF/request wiring. Returns false on any failure so the caller can
  // decide what "no valid session" means for it (e.g. an
  // interaction_required redirect) instead of this function deciding.
  const oneClickLogin = async (): Promise<boolean> => {
    try {
      const csrfToken = await ensureCsrfToken(AuthContextState.csrf_token);
      const requestConfig = generateRequestConfig(
        "put",
        "oneclicklogin",
        {
          deviceId: getDeviceId(),
          appId: getIdApp(),
        },
        {
          "X-CSRF-TOKEN": csrfToken,
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        }
      );
      const response = await axios(requestConfig);
      await handleLogIn(response.data);
      return true;
    } catch {
      return false;
    }
  };

  const handleLogOut = async (data: LogoutData) => {
    // docs/security-and-platform-work-plan.md Этап 7 — an HttpOnly cookie
    // can only be cleared by the backend (app/api/logout.py's
    // clear_session_cookies()); nothing left here for JS to remove. If this
    // runs without ever calling /logout (e.g. a failed refresh giving up),
    // the cookie just lingers until its own expiry — harmless, since the
    // session it names is already unusable either way.
    localStorage.removeItem("accessTokenExpAt");
    localStorage.removeItem("refreshTokenExpAt");

    const logout = data.logout;
    const urlCallback = data.urlCallback ?? null;
    const ref = referer();

    await setAuthContextState((prevState) => ({
      ...prevState,
      logout: logout ?? null,
      referer: ref ?? urlCallback ?? null,
    }));

    checkIsAuthenticated();
  };

  return (
    <AuthContext.Provider
      value={{
        AuthContextState,
        checkIsAuthenticated,
        redirectToApp,
        handleLogIn,
        handleLogOut,
        setAuthContextState,
        oneClickLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
